import { useState, useEffect } from "react";
import {
  X,
  Video,
  Clock,
  Calendar,
  Loader,
  AlertCircle,
  RefreshCw,
  Play,
  Check,
  HardDrive,
} from "lucide-react";
import { colors, fonts } from "../../styles/theme";
import {
  fetchZoomCloudRecordings,
  ingestZoomRecording,
} from "../../services/api";

export default function ZoomRecordingsDrawer({ open, onClose }) {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [ingestingId, setIngestingId] = useState(null);
  const [ingestedIds, setIngestedIds] = useState(new Set());

  const loadRecordings = async (opts = {}) => {
    try {
      const isLoadMore = !!opts.pageToken;
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError(null);
      }

      const result = await fetchZoomCloudRecordings({
        pageToken: opts.pageToken,
        pageSize: 30,
      });

      if (isLoadMore) {
        setRecordings((prev) => [...prev, ...(result.meetings || [])]);
      } else {
        setRecordings(result.meetings || []);
      }
      setTotalRecords(result.total_records || 0);
      setNextPageToken(result.next_page_token || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (open) loadRecordings();
  }, [open]);

  if (!open) return null;

  const handleIngest = async (rec) => {
    setIngestingId(rec.id);
    try {
      const result = await ingestZoomRecording(
        rec.id,
        rec.topic,
        rec.duration * 60,
        rec.type,
      );
      setIngestedIds((prev) => new Set(prev).add(rec.id));
      if (result.triggerWarning) {
        alert(`Recording saved but pipeline trigger failed: ${result.triggerWarning}`);
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setIngestingId(null);
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return "--";
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  const getTypeBadge = (type) => {
    const isWebinar = [5, 6, 9].includes(type);
    return isWebinar
      ? { label: "Webinar", bg: "rgba(45,140,255,0.10)", color: "#2D8CFF" }
      : { label: "Meeting", bg: "rgba(16,185,129,0.10)", color: colors.success };
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          zIndex: 99,
        }}
      />

      {/* Drawer */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: 560,
          background: colors.bgCard,
          borderLeft: `1px solid ${colors.border}`,
          zIndex: 100,
          display: "flex",
          flexDirection: "column",
          boxShadow: "-20px 0 60px rgba(0,0,0,0.12)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "24px 28px",
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Video size={20} color={colors.info} />
              <h2
                style={{
                  fontFamily: fonts.heading,
                  fontSize: 18,
                  color: colors.text,
                  margin: 0,
                }}
              >
                Zoom Recordings
              </h2>
              {totalRecords > 0 && (
                <span
                  style={{
                    fontSize: 11,
                    padding: "2px 8px",
                    borderRadius: 10,
                    background: "rgba(99,102,241,0.1)",
                    color: colors.info,
                    fontWeight: 600,
                  }}
                >
                  {totalRecords}
                </span>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button
                onClick={() => loadRecordings()}
                title="Refresh"
                style={{
                  background: "none",
                  border: "none",
                  color: colors.textSecondary,
                  cursor: "pointer",
                  padding: 4,
                }}
              >
                <RefreshCw size={16} />
              </button>
              <button
                onClick={onClose}
                style={{
                  background: "none",
                  border: "none",
                  color: colors.textSecondary,
                  cursor: "pointer",
                  padding: 4,
                }}
              >
                <X size={18} />
              </button>
            </div>
          </div>
          <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 8 }}>
            Cloud recordings from the last 30 days. Click Process to download and transcribe.
          </div>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "20px 28px",
          }}
        >
          {loading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: 40,
                color: colors.textSecondary,
                fontSize: 13,
              }}
            >
              <Loader
                size={16}
                className="spin"
              />
              Loading Zoom Recordings...
            </div>
          ) : error ? (
            <div
              style={{
                padding: 24,
                textAlign: "center",
                color: colors.error,
                fontSize: 13,
              }}
            >
              <AlertCircle
                size={24}
                style={{ marginBottom: 8, opacity: 0.6 }}
              />
              <div style={{ marginBottom: 8 }}>{error}</div>
              <button
                onClick={() => loadRecordings()}
                style={{
                  padding: "6px 16px",
                  background: "rgba(239,68,68,0.08)",
                  border: `1px solid ${colors.error}`,
                  borderRadius: 6,
                  color: colors.error,
                  fontSize: 12,
                  cursor: "pointer",
                  fontFamily: fonts.body,
                }}
              >
                Retry
              </button>
            </div>
          ) : recordings.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: 40,
                color: colors.textSecondary,
                fontSize: 13,
              }}
            >
              <Video
                size={28}
                style={{ marginBottom: 10, opacity: 0.4 }}
                color={colors.textSecondary}
              />
              <div style={{ marginBottom: 4 }}>No recordings found</div>
              <div style={{ fontSize: 11 }}>
                Cloud recordings from Zoom meetings and webinars will appear here.
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {recordings.map((rec) => (
                <RecordingCard
                  key={rec.uuid || rec.id}
                  rec={rec}
                  ingestingId={ingestingId}
                  ingestedIds={ingestedIds}
                  onIngest={handleIngest}
                  formatDuration={formatDuration}
                  formatDate={formatDate}
                  formatFileSize={formatFileSize}
                  getTypeBadge={getTypeBadge}
                />
              ))}

              {/* Load more */}
              {nextPageToken && (
                <div style={{ textAlign: "center", paddingTop: 8 }}>
                  <button
                    onClick={() => loadRecordings({ pageToken: nextPageToken })}
                    disabled={loadingMore}
                    style={{
                      padding: "8px 24px",
                      background: "rgba(99,102,241,0.08)",
                      border: `1px solid ${colors.border}`,
                      borderRadius: 6,
                      color: colors.info,
                      fontSize: 13,
                      fontFamily: fonts.body,
                      cursor: loadingMore ? "default" : "pointer",
                      opacity: loadingMore ? 0.6 : 1,
                    }}
                  >
                    {loadingMore ? "Loading..." : "Load More"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>    </>
  );
}

function RecordingCard({
  rec,
  ingestingId,
  ingestedIds,
  onIngest,
  formatDuration,
  formatDate,
  formatFileSize,
  getTypeBadge,
}) {
  const isIngesting = ingestingId === rec.id;
  const isIngested = ingestedIds.has(rec.id);
  const typeBadge = getTypeBadge(rec.type);

  // Sum MP4 file sizes for display
  const mp4Files = (rec.recording_files || []).filter(
    (f) => (f.file_extension || "").toUpperCase() === "MP4",
  );
  const completedMp4 = mp4Files.filter((f) => f.status === "completed");
  const isZoomProcessing = mp4Files.length === 0 || completedMp4.length === 0;
  const totalSize = mp4Files.reduce((sum, f) => sum + (f.file_size || 0), 0);
  const fileCount = (rec.recording_files || []).length;

  return (
    <div
      style={{
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        padding: "14px 16px",
        background: colors.bgCard,
      }}
    >
      {/* Title + type badge */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: colors.text,
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {rec.topic || "Untitled Recording"}
        </div>
        <span
          style={{
            fontSize: 10,
            padding: "2px 7px",
            borderRadius: 4,
            background: typeBadge.bg,
            color: typeBadge.color,
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          {typeBadge.label}
        </span>
      </div>

      {/* Meta row */}
      <div
        style={{
          display: "flex",
          gap: 12,
          fontSize: 11,
          color: colors.textSecondary,
          marginBottom: 10,
          flexWrap: "wrap",
        }}
      >
        {rec.start_time && (
          <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <Calendar size={10} />
            {formatDate(rec.start_time)}
          </span>
        )}
        {rec.duration > 0 && (
          <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <Clock size={10} />
            {formatDuration(rec.duration)}
          </span>
        )}
        {totalSize > 0 && (
          <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <HardDrive size={10} />
            {formatFileSize(totalSize)}
          </span>
        )}
        {fileCount > 0 && (
          <span>{fileCount} file{fileCount !== 1 ? "s" : ""}</span>
        )}
      </div>

      {/* Action buttons */}
      <div style={{ display: "flex", gap: 6 }}>
        <button
          onClick={() => onIngest(rec)}
          disabled={isIngesting || isIngested || isZoomProcessing}
          title={isZoomProcessing ? "Zoom is still processing this recording — try again later" : ""}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: "5px 10px",
            background: "none",
            border: `1px solid ${colors.border}`,
            borderRadius: 4,
            color: isIngested ? colors.success : isZoomProcessing ? colors.textSecondary : colors.info,
            fontSize: 11,
            fontFamily: fonts.body,
            cursor: isIngesting || isIngested || isZoomProcessing ? "default" : "pointer",
            opacity: isIngesting || isIngested || isZoomProcessing ? 0.5 : 1,
          }}
        >
          {isIngested ? <Check size={12} /> : <Play size={12} />}
          {isIngested ? "Queued" : isIngesting ? "Processing..." : isZoomProcessing ? "Zoom Processing..." : "Process"}
        </button>
        {rec.share_url && (
          <button
            onClick={() => window.open(rec.share_url, "_blank")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "5px 10px",
              background: "none",
              border: `1px solid ${colors.border}`,
              borderRadius: 4,
              color: colors.textSecondary,
              fontSize: 11,
              fontFamily: fonts.body,
              cursor: "pointer",
            }}
          >
            Open in Zoom
          </button>
        )}
      </div>
    </div>
  );
}
